/** Source kinds the chat shows with their own icon and label. */
// "attachment" and "google_drive": files research read from the chat and from Google Drive (MHS-797).
// "google_docs" | "google_sheets" | "google_slides": what the Google Workspace read tools opened (MHS-575).
const SOURCE_KINDS = [
  "rag",
  "web",
  "sharepoint",
  "kg",
  "conversation",
  "attachment",
  "google_drive",
  "google_docs",
  "google_sheets",
  "google_slides",
] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];

/** Other tools send their own kinds ("jira", "teams", ...); those show as "rag". */
export function sourceKind(type: string): SourceKind {
  return SOURCE_KINDS.find((kind) => kind === type) ?? "rag";
}

/** Sources behind an answer (tool_end metadata while streaming, messageMetadata once saved). See AGENTS.md "Source Tracking Contract". */
export interface ChatSource {
  /** A SourceKind or another tool's own kind (see `sourceKind`). */
  type: string;
  name: string;
  url?: string;
  snippet?: string;
  /** Web: when the page was published, as the search gave it (e.g. "Tue, 20 Oct 2026 09:00:06 GMT"). */
  publishedDate?: string;
  /** RAG: passages retrieved from this file. */
  chunks?: number;
  /** Knowledge graph (`kg`): the wiki page, opened in its space's Knowledge area. */
  wiki?: { spaceUuid: string; path: string; spaceName?: string; nodeUuid?: string };
  /** Knowledge (`rag`): the stored file, opened beside the chat. Absent on answers saved before MHS-1040. */
  file?: SourceFile;
  /** Research: the report cites it (true) or only consulted it (false). Absent on other answers. */
  cited?: boolean;
}

/** A file an answer used: a space's own file (`spaceUuid`) opens in the space, any other in its data source. */
export interface SourceFile {
  uuid: string;
  name: string;
  collectionUuid: string;
  spaceUuid?: string;
}

/**
 * A message's sources by number, in stored order: `[n]` in the answer is entry `n - 1`. `null` holds the place of
 * an entry with nothing to show it by, so the numbers after it still line up.
 */
export type NumberedSources = readonly (ChatSource | null)[];

function wikiOf(r: Record<string, unknown>): ChatSource["wiki"] {
  const spaceUuid = str(r.space_uuid) ?? str(r.spaceUuid);
  const path = str(r.path);
  if (r.type !== "kg" || !spaceUuid || !path) return undefined;
  return { spaceUuid, path, spaceName: str(r.space), nodeUuid: str(r.node_uuid) ?? str(r.nodeUuid) };
}

const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : undefined);

function fileOf(r: Record<string, unknown>, name: string): SourceFile | undefined {
  const uuid = str(r.file_uuid);
  const spaceUuid = str(r.space_uuid);
  const collectionUuid = str(r.collection_uuid);
  if (r.type !== "rag" || !uuid || !collectionUuid) return undefined;
  return { uuid, name, collectionUuid, spaceUuid };
}

/** One raw source dict, normalized; null when it has no name, title or URL to show. */
function parseSource(item: unknown): ChatSource | null {
  if (!item || typeof item !== "object") return null;
  const r = item as Record<string, unknown>;
  const name = str(r.name) ?? str(r.title) ?? str(r.url);
  if (!name) return null;
  return {
    type: str(r.type) ?? "rag",
    name,
    url: str(r.url),
    snippet: str(r.snippet),
    publishedDate: str(r.published_date) ?? str(r.publishedDate),
    chunks: typeof r.chunks === "number" ? r.chunks : undefined,
    wiki: wikiOf(r),
    file: fileOf(r, name),
    cited: typeof r.cited === "boolean" ? r.cited : undefined,
  };
}

/** Normalizes raw source dicts and merges repeats (same file/page cited by several tool calls). */
export function parseSources(raw: unknown): ChatSource[] {
  if (!Array.isArray(raw)) return [];
  const merged = new Map<string, ChatSource>();
  for (const item of raw) {
    const source = parseSource(item);
    if (!source) continue;
    // Wiki page and file names repeat across spaces and data sources: tell them apart by page or file.
    const key = `${source.type}|${source.wiki?.nodeUuid ?? source.wiki?.path ?? source.file?.uuid ?? source.url ?? source.name}`;
    const existing = merged.get(key);
    if (existing) {
      existing.chunks = (existing.chunks ?? 0) + (source.chunks ?? 0) || undefined;
      existing.snippet ??= source.snippet;
      existing.publishedDate ??= source.publishedDate;
    } else {
      merged.set(key, source);
    }
  }
  return [...merged.values()];
}

/** The sources as stored, one entry per position (see NumberedSources); for a saved message, its `sources`. */
export function numberedSources(raw: unknown): NumberedSources {
  return Array.isArray(raw) ? raw.map((item) => parseSource(item)) : [];
}

// Python's `src.get(key)` / `a or b` inside an f-string, so keys compare equal exactly when the backend's do. The
// fields are strings or numbers; anything else can't match a backend key.
const keyOf = (value: unknown) => (typeof value === "string" || typeof value === "number" ? String(value) : "");
const keyOfEither = (value: unknown, fallback: unknown) => keyOf(value || fallback);

/** backend/src/services/ai/engine.py `_source_dedup_key`: what the reply numbers and saves its sources by. */
function backendKey(source: Record<string, unknown>): string {
  const type = typeof source.type === "string" ? source.type : "unknown";
  switch (type) {
    case "rag":
      return `rag:${keyOfEither(source.file_id, source.name)}`;
    case "web":
      return `web:${keyOf(source.url)}`;
    case "sharepoint":
      return `sp:${keyOfEither(source.url, source.name)}`;
    case "kg":
      return `kg:${keyOfEither(source.node_uuid, source.name)}`;
    default:
      return `${type}:${keyOfEither(source.name, source.url)}`;
  }
}

/**
 * A streaming reply's sources (every tool result's, in arrival order) deduped the way the backend saves them
 * (`_deduplicate_sources`): the first of each keeps its place and fields, and a repeated file adds its passages.
 * So `[n]` points at the same source while the reply streams as once it's saved.
 */
export function dedupeSources(raw: readonly unknown[]): Record<string, unknown>[] {
  const byKey = new Map<string, Record<string, unknown>>();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const source = item as Record<string, unknown>;
    const key = backendKey(source);
    const first = byKey.get(key);
    if (!first) {
      byKey.set(key, { ...source });
    } else if (source.type === "rag" && typeof source.chunks === "number") {
      first.chunks = (typeof first.chunks === "number" ? first.chunks : 0) + source.chunks;
    }
  }
  return [...byKey.values()];
}
